/**
 * Approval (HITL) card styling on the brand grammar: flat, hairline borders, control radius.
 *
 * The card keeps its warm `CARD_BACKGROUND`; a Theo-violet leading edge marks it as an action
 * Theo is proposing. One solid violet primary per card; everything else is a neutral outline.
 */
import type { SxProps, Theme } from "@mui/material";
import { NEUTRAL, RADIUS, VIOLET } from "../../styles/brandTokens";

/** The approval card's own warm surface. Deliberately kept from the original design. */
export const CARD_BACKGROUND = "#FFFBF8";

/** Theo-violet leading edge, drawn inside the border so it follows the card's corners. */
export const CARD_EDGE = `inset 3px 0 0 ${VIOLET.theo}`;

interface ApprovalButtonOptions {
  compact: boolean;
  fontSize: number;
}

function baseButtonSx({ compact, fontSize }: ApprovalButtonOptions) {
  return {
    minHeight: compact ? 30 : 34,
    p: compact ? "4px 10px !important" : "6px 14px !important",
    fontSize: `${fontSize}px`,
    fontFamily: "var(--theo-font-sans)",
    fontWeight: 600,
    textTransform: "none" as const,
    borderRadius: `${RADIUS.control}px`,
    boxShadow: "none",
    "& .MuiButton-startIcon": { mr: 0.75 },
    "&.Mui-focusVisible": { outline: `2px solid ${VIOLET.main}`, outlineOffset: 2 },
  };
}

/** The one solid action on the card (Continue / I've sent the email). */
export function approvalPrimaryButtonSx(options: ApprovalButtonOptions): SxProps<Theme> {
  return {
    ...baseButtonSx(options),
    background: VIOLET.main,
    color: "#FFFFFF !important",
    "&:hover": { background: VIOLET.dark, boxShadow: "none" },
    "&:disabled": {
      background: NEUTRAL.hover,
      color: `${NEUTRAL.textMuted} !important`,
    },
  };
}

/** Neutral outline for every secondary action (Cancel, Schedule). */
export function approvalSecondaryButtonSx(options: ApprovalButtonOptions): SxProps<Theme> {
  return {
    ...baseButtonSx(options),
    backgroundColor: NEUTRAL.surface,
    border: `1px solid ${NEUTRAL.borderStrong}`,
    color: `${NEUTRAL.text} !important`,
    "&:hover": {
      backgroundColor: NEUTRAL.hover,
      borderColor: NEUTRAL.borderStrong,
      boxShadow: "none",
    },
    "&:disabled": {
      backgroundColor: NEUTRAL.surface,
      borderColor: NEUTRAL.border,
      color: `${NEUTRAL.textMuted} !important`,
    },
  };
}

/** Outline whose label is a link out (open the email draft, download a letter). */
export function approvalLinkButtonSx(options: ApprovalButtonOptions): SxProps<Theme> {
  return {
    ...(approvalSecondaryButtonSx(options) as object),
    color: `${VIOLET.main} !important`,
    width: "fit-content",
    maxWidth: "100%",
    whiteSpace: "nowrap",
  };
}
